<template>
  <v-row
    align="center"
  >
    <v-col
      class="py-0"
      v-cloak
      @dragover.prevent
      @dragenter="handleDragging($event, true)"
      @dragleave="handleDragging($event, false)"
      @drop.prevent="addFile"
      @paste="addFile"
    >
      <div>
        <template v-if="$slots.required">
        <slot name="required"></slot>
        </template>
        {{ this.label }}
        <v-menu>
          <template v-slot:activator="{ on: menu, attrs }">
            <v-tooltip right>
              <template v-slot:activator="{ on: tooltip }">
                <v-icon
                  small
                  v-bind="attrs"
                  v-on="{ ...tooltip, ...menu }"
                >
                  mdi-information
                </v-icon>
              </template>
              <span>{{$t('components.file.maxFiles')}}</span>
              <v-spacer/>
              <span>{{$t('components.file.maxSize')}}</span>
            </v-tooltip>
          </template>
        </v-menu>
      </div>

      <v-file-input
        class="mt-0 pt-0"
        ref="fileInput"
        v-model="files"
        @change="files = handleChange($event, prevFiles)"
        :placeholder="filePlaceHolder"
        :disabled="disabled"
        :multiple="multiple"
        @click:clear="clearFiles"
        :outlined="!disabled"
        :filled="disabled"
        prepend-icon
        :rules="rules"
      >
        <template v-slot:prepend-inner>
          <v-icon size="23" class="mx-3">mdi-cloud-upload-outline</v-icon>
        </template>
        <template  v-slot:selection="{ index, file }">
          <!-- added files overview -->
          <v-row no-gutters class="col-lg-6 col-xl-3">
            <v-col
              :key="`file-col-${index}`"
              :cols="12"
              class="py-2"
            >
              <v-card class="img-preview mb-4 mr-4"
                :key="`file-${index}`"
                :value="file.name"
                max-width="200"
              >
                <!-- image preview type -->
                <v-img
                  v-if="isImage(file)"
                  :src="createUrl(file)"
                  class="white--text align-end"
                  gradient="to bottom, rgba(0,0,0,.1), rgba(0,0,0,.5)"
                >
                </v-img>

                <!-- other files preview type -->
                <v-sheet
                  v-else
                  class="d-flex justify-center align-center"
                >
                  <v-icon x-large>mdi-file</v-icon>
                </v-sheet>

                <v-card-actions>
                  <small class="text-wrap">{{ file.name }}</small>
                  <v-spacer></v-spacer>
                  <v-btn
                    v-if="isImage(file)"
                    :disabled="disabled"
                    icon
                    @click.stop="editImage(file, `file-${index}`)"
                  >
                    <v-icon >mdi-image-edit-outline</v-icon>
                  </v-btn>
                  <v-btn
                    @click.stop="deleteFile(file)"
                    :disabled="disabled"
                    icon
                  >
                    <v-icon>mdi-trash-can</v-icon>
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>

          <!-- image edition modal -->
          <ModalDialog
            :dialog="editorDialog"
            @input="editorDialog = $event"
            :key="`edit-image-${index}`"
          >
            <template slot="title">
              Edición de imagen
            </template>

            <template slot="body">
              <EditImage
                v-if="editionImage"
                :src="createUrl(editionImage)"
                :name="editionImage.name"
                @change="changeCropImage"
                @cancel="editorDialog = false"
              ></EditImage>
            </template>

          </ModalDialog>
        </template>
      </v-file-input>

      <!-- drag&drop container -->
      <v-expand-transition>
        <v-sheet
          rounded
          v-show="dropZone && !disabled"
          height="100"
          color="indigo lighten-5"
          class="mb-4"
        >
          <div class="d-flex justify-center align-center fill-height">
            <v-icon x-large>mdi-archive-arrow-up-outline</v-icon>
            <span>Suelta los archivos aquí</span>
          </div>
        </v-sheet>
      </v-expand-transition>
    </v-col>

    <v-btn
      v-if="cameraEnabled"
      icon
      color="primary"
      @click="captureImage()"
    >
      <v-icon
        size="30"
        class="mb-4"
      >
        mdi-camera
      </v-icon>
    </v-btn>
  </v-row>
</template>

<script>
import Vue from 'vue';
import moment from 'moment';
import ModalDialog from '@/components/ModalDialog.vue';
import EditImage from '@/components/EditImage.vue';

const compareFiles = (file1, file2) => ['name', 'type'].every((key) => file1[key] === file2[key]);

export default {
  components: {
    ModalDialog,
    EditImage,
  },

  props: {
    label: String,
    params: {
      type: Object,
      default: () => ({}),
    },
    disabled: Boolean,
    config: Object,
    multiple: {
      type: Boolean,
      default: () => true,
    },
    rules: {
      type: Array,
      default: () => [],
    },
  },

  computed: {
    cameraEnabled() {
      const parentConfig = (this.$parent.config || {});
      const camEnabled = parentConfig.cameraEnabled === 'true';
      const getter = this.$store.getters['globalState/get'];
      return getter('deviceOS') != null && camEnabled;
    },
    filePlaceHolder() {
      if (this.cameraEnabled) {
        return this.$t('components.file.placeholderApp');
      }
      return this.$t('components.file.placeholder');
    },
  },

  data({ multiple }) {
    return {
      files: multiple ? [] : null,
      prevFiles: multiple ? [] : null,
      previews: [],
      dragging: null,
      dropZone: false,
      editorDialog: false,
      editionImage: null,
      deletionFlag: false,
    };
  },

  watch: {
    files(val) {
      this.previews = (this.multiple ? val : [val]).map((file) => ({
        name: file.name,
        type: file.type,
        url: URL.createObjectURL(file),
      }));

      this.deletionFlag = false;
      this.prevFiles = val;
    },
  },

  methods: {
    handleChange(val, oldVal) {
      // console.log('fileList', this.deletionFlag, val, oldVal);
      if (this.deletionFlag) {
        return val;
      }
      if (!val.length) {
        // console.log('returning oldval...', val, oldVal);
        return oldVal;
      }
      let fileList = val;

      if (this.multiple) {
        fileList = val.concat(oldVal
          .filter((file) => !val.some((newFile) => compareFiles(newFile, file))));
      }
      return fileList;
    },

    handleDragging(evt, newval) {
      if (this.dragging !== newval) {
        this.dragging = newval;
      } else {
        this.dropZone = newval;
        this.dragging = newval;
      }
      evt.preventDefault();
      evt.stopPropagation();
    },

    async addFile(evt) {
      const droppedFilesNew = [];
      const droppedFiles = (evt.dataTransfer || evt.clipboardData).files;
      const newName = this.getNewName(droppedFiles[0].name);
      const droppedFilesChangeName = Object.defineProperty(droppedFiles[0], 'name', {
        writable: true,
        value: newName,
      });
      droppedFilesNew.push(droppedFilesChangeName);

      // this tip, convert FileList to array
      if (this.multiple) {
        ([...droppedFilesNew])
          .filter((f) => !this.files.some((file) => compareFiles(f, file)))
          .forEach((f) => {
            this.files.push(f);
          });
      } else {
        const [firstFile] = [...droppedFilesNew];
        this.files = firstFile;
      }

      this.dropZone = false;
      evt.preventDefault();
    },

    clearFiles() {
      this.deletionFlag = true;
      this.files = [];
    },

    deleteFile(file) {
      this.deletionFlag = true;
      this.files = this.files.filter((item) => item.name !== file.name);
    },

    isImage(file) {
      return ['image/gif', 'image/jpeg', 'image/png'].includes(file.type);
    },

    editImage(file) {
      this.editionImage = file;
      this.editorDialog = true;
    },

    async changeCropImage({ name, blob }) {
      const index = this.files.findIndex((f) => compareFiles(f, this.editionImage));
      const response = await fetch(blob);
      const getBlob = await response.blob();
      this.files[index] = new File([getBlob], name, { type: this.files[index].type });
      // file = new File([blob], name, { type: file.type });
      this.editorDialog = false;

      // TODO mount new File from blob
      /* this.editions.push({
        file,
        edited: new File...
      }) */
    },

    createUrl(file) {
      return URL.createObjectURL(file);
    },

    getNewName(name) {
      const contentName = name.split('.');
      const today = moment().format('YYYYMMDD_hhmmss');
      return `${ contentName[0] }_${ today }.${ contentName[1] }`;
    },

    captureImage() {
      Vue.prototype.$flutterPort.postMessage('flutterCaptureImage');
    },

    addFileFromFlutter(flutterObject) {
      try {
        const byteCharacters = atob(flutterObject.data);
        const byteNumbers = new Array(byteCharacters.length);
        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const file = new File([byteArray], flutterObject.name, { type: flutterObject.type });
        if (this.multiple) {
          this.files.push(file);
        } else {
          this.files = file;
        }
      } catch (error) {
        console.log('Error al parsear la información de flutter', error);
      }
    },
  },

  async created() {
    if (this.cameraEnabled && window.flutter_inappwebview) {
      this.$root.$on('flutter:fileCapture', (event) => {
        console.log(`Flutter mensaje desde móvil: ${event}`);
        this.addFileFromFlutter(event);
      }, false);
    }
  },
};
</script>

<style lang="scss" scoped>
.text-wrap{
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}
</style>
